import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Search, Barcode, Printer, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import BarcodeComponent from 'react-barcode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};


const Books = () => {
  const [books, setBooks] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [racks, setRacks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedBookForBarcode, setSelectedBookForBarcode] = useState(null);
  const [barcodeCopies, setBarcodeCopies] = useState([]);
  const [barcodeCopiesLoading, setBarcodeCopiesLoading] = useState(false);
  const [manualAccessions, setManualAccessions] = useState([]);
  const [showManualAccessions, setShowManualAccessions] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const barcodeRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  
  const [formData, setFormData] = useState({
    title: '',
    isbn: '',
    call_number: '',
    accession_number: '',
    author_id: '',
    author_name: '',
    publisher_id: '',
    publisher_name: '',
    category_id: '',
    category_name: '',
    rack_id: '',
    rack_number: '',
    total_copies: 1,
    available_copies: 1,
    price: 0,
    description: '',
    publication_year: '',
    is_active: true
  });

  useEffect(() => {
    fetchBooks();
    fetchAuthors();
    fetchCategories();
    fetchPublishers();
    fetchRacks();
  }, []);

  const fetchBooks = async () => {
    try {
      // Fetch all books with high limit for full list
      const response = await axios.get(`${API_URL}/api/library/books?limit=10000`, { headers: getAuthHeaders() });
      // Handle both old format (array) and new format ({data: [], total: n})
      if (Array.isArray(response.data)) {
        setBooks(response.data);
      } else if (response.data?.data) {
        setBooks(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setBooks([]);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
      setBooks([]);
    }
  };

  const fetchAuthors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/authors`, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      setAuthors(data.filter(author => author.is_active));
    } catch (error) {
      console.error('Error fetching authors:', error);
      setAuthors([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/categories`, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      setCategories(data.filter(cat => cat.is_active));
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchPublishers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/publishers`, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      setPublishers(data.filter(pub => pub.is_active));
    } catch (error) {
      console.error('Error fetching publishers:', error);
      setPublishers([]);
    }
  };

  const fetchRacks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/racks`, { headers: getAuthHeaders() });
      const data = Array.isArray(response.data) ? response.data : [];
      setRacks(data.filter(rack => rack.is_active));
    } catch (error) {
      console.error('Error fetching racks:', error);
      setRacks([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate author is selected
    if (!formData.author_name || !formData.author_name.trim()) {
      alert('Author is required');
      return;
    }
    
    setLoading(true);

    try {
      const url = editingBook 
        ? `${API_URL}/api/library/books/${editingBook.id}`
        : `${API_URL}/api/library/books`;
      
      const payload = {
        ...formData,
        publication_year: formData.publication_year ? parseInt(formData.publication_year) : null
      };

      if (!editingBook && manualAccessions.some(a => a && a.trim())) {
        const list = Array.from({ length: formData.total_copies }, (_, i) => (manualAccessions[i] || '').trim());
        if (!list[0] && formData.accession_number) list[0] = formData.accession_number.trim();
        payload.accession_numbers = list;
      }

      if (editingBook) {
        await axios.put(url, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(url, payload, { headers: getAuthHeaders() });
      }

      fetchBooks();
      handleCloseModal();
      alert('Book saved successfully!');
    } catch (error) {
      console.error('Error saving book:', error);
      alert(error.response?.data?.detail || 'Failed to save book');
    } finally {
      setLoading(false);
    }
  };

  // Bulk Import Functions
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library/books/bulk-import/template`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'book_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
        alert('Please select an Excel (.xlsx, .xls) or CSV file');
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  const handleBulkImport = async () => {
    if (!importFile) {
      alert('Please select a file to import');
      return;
    }

    setImportLoading(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API_URL}/api/library/books/bulk-import`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      if (response.data.results.imported > 0) {
        fetchBooks(); // Refresh book list
      }
    } catch (error) {
      console.error('Error importing books:', error);
      setImportResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to import books',
        results: { errors: [{ error: error.response?.data?.detail || 'Unknown error' }] }
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleCloseImportModal = () => {
    setIsImportModalOpen(false);
    setImportFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this book?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/library/books/${id}`, { headers: getAuthHeaders() });
      fetchBooks();
      alert('Book deleted successfully!');
    } catch (error) {
      console.error('Error deleting book:', error);
      alert('Failed to delete book');
    }
  };

  const handleEdit = (book) => {
    setEditingBook(book);
    setFormData({
      title: book.title,
      isbn: book.isbn,
      call_number: book.call_number || '',
      author_id: book.author_id,
      author_name: book.author_name,
      publisher_id: book.publisher_id,
      publisher_name: book.publisher_name,
      category_id: book.category_id,
      category_name: book.category_name,
      rack_id: book.rack_id,
      rack_number: book.rack_number,
      total_copies: book.total_copies,
      available_copies: book.available_copies,
      price: book.price,
      description: book.description || '',
      publication_year: book.publication_year || '',
      is_active: book.is_active
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBook(null);
    setFormData({
      title: '',
      isbn: '',
      call_number: '',
      accession_number: '',
      author_id: '',
      author_name: '',
      publisher_id: '',
      publisher_name: '',
      category_id: '',
      category_name: '',
      rack_id: '',
      rack_number: '',
      total_copies: 1,
      available_copies: 1,
      price: 0,
      description: '',
      publication_year: '',
      is_active: true
    });
    setManualAccessions([]);
    setShowManualAccessions(false);
  };

  const handleAuthorChange = (authorId) => {
    const author = authors.find(a => a.id === authorId);
    setFormData({
      ...formData,
      author_id: authorId,
      author_name: author ? author.name : ''
    });
  };

  const handleCategoryChange = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    setFormData({
      ...formData,
      category_id: categoryId,
      category_name: category ? category.name : ''
    });
  };

  const handlePublisherChange = (publisherId) => {
    const publisher = publishers.find(p => p.id === publisherId);
    setFormData({
      ...formData,
      publisher_id: publisherId,
      publisher_name: publisher ? publisher.name : ''
    });
  };

  const handleRackChange = (rackId) => {
    const rack = racks.find(r => r.id === rackId);
    setFormData({
      ...formData,
      rack_id: rackId,
      rack_number: rack ? rack.rack_number : ''
    });
  };

  const handleShowBarcode = async (book) => {
    setSelectedBookForBarcode(book);
    setBarcodeCopies([]);
    setBarcodeCopiesLoading(true);
    setIsBarcodeModalOpen(true);
    try {
      const response = await axios.get(`${API_URL}/api/library/books/${book.id}/copies`, { headers: getAuthHeaders() });
      setBarcodeCopies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setBarcodeCopies([]);
    } finally {
      setBarcodeCopiesLoading(false);
    }
  };

  const handlePrintBarcode = () => {
    const printContent = barcodeRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Barcodes - ${selectedBookForBarcode?.title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 10px; }
            .grid, [class*="grid"] { display: block; }
            .barcode-container { border: 1px dashed #ccc; padding: 10px 15px; display: inline-block; margin: 6px; text-align: center; vertical-align: top; page-break-inside: avoid; }
            .book-title { font-size: 12px; margin-bottom: 4px; font-weight: bold; }
            .book-info { font-size: 10px; color: #666; margin-bottom: 4px; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const filteredBooks = books.filter(book =>
    book.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.author_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.isbn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.barcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.accession_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    book.call_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalRecords = filteredBooks.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalRecords);
  const paginatedBooks = filteredBooks.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Book Management</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="generate-missing-barcodes-btn" onClick={async () => {
              try {
                const response = await axios.post(`${API_URL}/api/library/books/generate-barcodes`, {}, { headers: getAuthHeaders() });
                toast.success(response.data?.message || 'Barcodes generated');
                fetchBooks();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to generate barcodes');
              }
            }}>
              <Barcode className="w-4 h-4 mr-2" />
              Fix Missing Barcodes
            </Button>
            <Button variant="outline" onClick={() => setIsImportModalOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <Button onClick={async () => {
              setIsModalOpen(true);
              // Auto-fetch next accession number for new books
              try {
                const response = await axios.get(`${API_URL}/api/library/books/next-accession-number`, { headers: getAuthHeaders() });
                if (response.data?.accession_number) {
                  setFormData(prev => ({ ...prev, accession_number: response.data.accession_number }));
                }
              } catch (err) {
                console.error('Error fetching accession number:', err);
              }
            }}>
              <Plus className="w-4 h-4 mr-2" />
              Add Book
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by title, author, ISBN, barcode, accession number or call number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-2">Title</th>
                  <th className="text-left p-2">Author</th>
                  <th className="text-left p-2">ISBN</th>
                  <th className="text-left p-2">Call No.</th>
                  <th className="text-left p-2">Barcode</th>
                  <th className="text-left p-2">Category</th>
                  <th className="text-center p-2">Copies</th>
                  <th className="text-center p-2">Available</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBooks.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center p-4 text-gray-500">
                      No books found. Click &quot;Add Book&quot; to create one.
                    </td>
                  </tr>
                ) : (
                  paginatedBooks.map((book) => (
                    <tr key={book.id} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-medium">{book.title}</td>
                      <td className="p-2">{book.author_name}</td>
                      <td className="p-2 font-mono text-xs">{book.isbn}</td>
                      <td className="p-2 font-mono text-xs" data-testid={`book-call-number-${book.id}`}>{book.call_number || '-'}</td>
                      <td className="p-2">
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{book.barcode || '-'}</span>
                      </td>
                      <td className="p-2">{book.category_name}</td>
                      <td className="p-2 text-center">{book.total_copies}</td>
                      <td className="p-2 text-center">
                        <span className={`font-medium ${book.available_copies > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {book.available_copies}
                        </span>
                      </td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleShowBarcode(book)} title="View Barcode">
                            <Barcode className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleEdit(book)} title="Edit">
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(book.id)} title="Delete">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalRecords > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
                <span>entries</span>
                <span className="ml-4">
                  Showing {startIndex + 1} to {endIndex} of {totalRecords} books
                </span>
              </div>
              
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  
                  <span className="px-3 py-1 text-sm">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBook ? 'Edit Book' : 'Add New Book'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium mb-2 min-h-[2.5rem]">Book Title *</label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Harry Potter and the Philosopher's Stone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 min-h-[2.5rem]">ISBN</label>
                <Input
                  value={formData.isbn}
                  onChange={(e) => setFormData({ ...formData, isbn: e.target.value })}
                  placeholder="e.g., 978-0747532699 (Optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 min-h-[2.5rem] leading-tight">
                  Accession Number
                  {!editingBook && <span className="block text-xs text-gray-400 font-normal">(Auto-generated, editable)</span>}
                </label>
                <Input
                  data-testid="accession-number-input"
                  value={formData.accession_number}
                  onChange={(e) => setFormData({ ...formData, accession_number: e.target.value })}
                  placeholder={editingBook ? "Not editable for existing books" : "e.g., ACC-2026-00001"}
                  disabled={!!editingBook}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Call Number <span className="text-xs text-gray-400 ml-1">(Optional · Searchable)</span>
                </label>
                <Input
                  data-testid="book-call-number-input"
                  value={formData.call_number}
                  onChange={(e) => setFormData({ ...formData, call_number: e.target.value })}
                  placeholder="e.g., 005.133 / RAM or QA76.73.J38"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Author *</label>
                <Select value={formData.author_id} onValueChange={handleAuthorChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select author" />
                  </SelectTrigger>
                  <SelectContent>
                    {authors.map((author) => (
                      <SelectItem key={author.id} value={author.id}>
                        {author.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category *</label>
                <Select value={formData.category_id} onValueChange={handleCategoryChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Publisher *</label>
                <Select value={formData.publisher_id} onValueChange={handlePublisherChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select publisher" />
                  </SelectTrigger>
                  <SelectContent>
                    {publishers.map((publisher) => (
                      <SelectItem key={publisher.id} value={publisher.id}>
                        {publisher.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Rack *</label>
                <Select value={formData.rack_id} onValueChange={handleRackChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select rack" />
                  </SelectTrigger>
                  <SelectContent>
                    {racks.map((rack) => (
                      <SelectItem key={rack.id} value={rack.id}>
                        {rack.rack_number} - {rack.location}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Total Copies *</label>
                <Input
                  type="number"
                  required
                  min="1"
                  value={formData.total_copies}
                  onChange={(e) => {
                    const total = parseInt(e.target.value) || 1;
                    setFormData({ 
                      ...formData, 
                      total_copies: total,
                      available_copies: total // Auto-set available copies to total for new books
                    });
                    setManualAccessions(prev => {
                      const next = [...prev];
                      next.length = total;
                      return Array.from(next, v => v || '');
                    });
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Price ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Publication Year</label>
                <Input
                  type="number"
                  min="1800"
                  max={new Date().getFullYear() + 1}
                  value={formData.publication_year}
                  onChange={(e) => setFormData({ ...formData, publication_year: e.target.value })}
                  placeholder="e.g., 1997"
                />
              </div>
            </div>

            {!editingBook && formData.total_copies > 1 && (
              <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 space-y-2" data-testid="manual-accession-section">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">
                    Copy Accession Numbers
                    <span className="block text-xs text-gray-500 font-normal">Khali rakhle auto-generate hobe · Manually type o korte paren</span>
                  </label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="autofill-accessions-btn"
                      onClick={() => {
                        const start = (formData.accession_number || '').trim();
                        if (!start) { toast.error('Age Starting Accession Number (upore) din'); return; }
                        const m = start.match(/^(.*?)(\d+)$/);
                        const list = [];
                        for (let i = 0; i < formData.total_copies; i++) {
                          if (m) {
                            const num = String(parseInt(m[2], 10) + i).padStart(m[2].length, '0');
                            list.push(`${m[1]}${num}`);
                          } else {
                            list.push(i === 0 ? start : `${start}-${i + 1}`);
                          }
                        }
                        setManualAccessions(list);
                        setShowManualAccessions(true);
                      }}
                    >
                      Auto Fill
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      data-testid="toggle-manual-accessions-btn"
                      onClick={() => {
                        setShowManualAccessions(!showManualAccessions);
                        if (!showManualAccessions && manualAccessions.length !== formData.total_copies) {
                          setManualAccessions(Array.from({ length: formData.total_copies }, (_, i) => manualAccessions[i] || ''));
                        }
                      }}
                    >
                      {showManualAccessions ? 'Hide' : 'Manual Entry'}
                    </Button>
                  </div>
                </div>
                {showManualAccessions && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                    {Array.from({ length: formData.total_copies }, (_, i) => (
                      <div key={i}>
                        <label className="text-xs text-gray-500">Copy {i + 1}</label>
                        <Input
                          data-testid={`copy-accession-input-${i + 1}`}
                          value={manualAccessions[i] || ''}
                          onChange={(e) => {
                            const next = [...manualAccessions];
                            next.length = formData.total_copies;
                            next[i] = e.target.value;
                            setManualAccessions(Array.from(next, v => v || ''));
                          }}
                          placeholder="Auto"
                          className="h-8 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description of the book..."
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="is_active" className="text-sm font-medium">Active</label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : editingBook ? 'Update Book' : 'Add Book'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Barcode Modal */}
      <Dialog open={isBarcodeModalOpen} onOpenChange={setIsBarcodeModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Barcode className="w-5 h-5" />
              Copy Barcodes {barcodeCopies.length > 0 && `(${barcodeCopies.length} ${barcodeCopies.length === 1 ? 'copy' : 'copies'})`}
            </DialogTitle>
          </DialogHeader>
          {selectedBookForBarcode && (
            <div className="space-y-4">
              {barcodeCopiesLoading ? (
                <p className="text-center text-sm text-gray-500 py-6" data-testid="barcode-copies-loading">Loading copies...</p>
              ) : (
                <div ref={barcodeRef} className="bg-white" data-testid="barcode-labels-container">
                  {barcodeCopies.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {barcodeCopies.map((copy) => (
                        <div key={copy.id} className="barcode-container text-center border border-dashed border-gray-300 rounded-lg p-3" data-testid={`barcode-label-${copy.accession_number}`}>
                          <p className="book-title font-semibold text-sm mb-1">{selectedBookForBarcode.title}</p>
                          <p className="book-info text-xs text-gray-500 mb-1">
                            Copy #{copy.copy_number} | Rack: {selectedBookForBarcode.rack_number || '-'}
                            {copy.status !== 'Available' && ` | ${copy.status}`}
                          </p>
                          <BarcodeComponent
                            value={copy.accession_number}
                            width={1.3}
                            height={45}
                            fontSize={12}
                            margin={4}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="barcode-container text-center border border-dashed border-gray-300 rounded-lg p-4">
                      <p className="book-title font-semibold text-sm mb-1">{selectedBookForBarcode.title}</p>
                      <p className="book-info text-xs text-gray-500 mb-2">
                        {selectedBookForBarcode.author_name} | Rack: {selectedBookForBarcode.rack_number}
                      </p>
                      {selectedBookForBarcode.barcode ? (
                        <BarcodeComponent
                          value={selectedBookForBarcode.barcode}
                          width={1.5}
                          height={50}
                          fontSize={12}
                          margin={5}
                        />
                      ) : (
                        <p className="text-xs text-gray-400">No copies or barcode found for this book</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-gray-500">ISBN:</span> {selectedBookForBarcode.isbn}</div>
                  <div><span className="text-gray-500">Total Copies:</span> {barcodeCopies.length || selectedBookForBarcode.total_copies}</div>
                  <div><span className="text-gray-500">Category:</span> {selectedBookForBarcode.category_name}</div>
                  <div><span className="text-gray-500">Price:</span> ₹{selectedBookForBarcode.price}</div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsBarcodeModalOpen(false)}>
                  Close
                </Button>
                <Button onClick={handlePrintBarcode} data-testid="print-all-barcodes-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print All Barcodes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Import Modal */}
      <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Bulk Import Books
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">Instructions:</h4>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Download the template file</li>
                <li>Fill in book details (Title & Author are required)</li>
                <li>Upload the filled file</li>
                <li>Authors, Publishers, Categories will be auto-created if not exists</li>
              </ol>
            </div>

            {/* Download Template */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Download sample template:</span>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Select Excel/CSV File:
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-medium
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  cursor-pointer border rounded-lg"
              />
              {importFile && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Selected: {importFile.name}
                </p>
              )}
            </div>

            {/* Import Result */}
            {importResult && (
              <div className={`p-4 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-start gap-2">
                  {importResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className={`font-medium ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {importResult.message}
                    </p>
                    {importResult.results && (
                      <div className="mt-2 text-sm">
                        <p className="text-gray-600">
                          Total Rows: {importResult.results.total_rows} | 
                          Imported: <span className="text-green-600 font-medium">{importResult.results.imported}</span> | 
                          Skipped: <span className="text-red-600 font-medium">{importResult.results.skipped}</span>
                        </p>
                        {importResult.results.errors && importResult.results.errors.length > 0 && (
                          <div className="mt-2 max-h-32 overflow-y-auto">
                            <p className="font-medium text-red-700">Errors:</p>
                            <ul className="list-disc list-inside text-red-600">
                              {importResult.results.errors.slice(0, 10).map((err, idx) => (
                                <li key={idx}>
                                  {err.row ? `Row ${err.row}: ` : ''}{err.error}
                                </li>
                              ))}
                              {importResult.results.errors.length > 10 && (
                                <li>...and {importResult.results.errors.length - 10} more errors</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseImportModal}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkImport} 
              disabled={!importFile || importLoading}
            >
              {importLoading ? (
                <>
                  <Upload className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Books
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Books;
